/**
 * Consumer-facing copy + demo data for tablet app hubs.
 * Keep settlement / wallet / process jargon in `technicalNotes` only.
 */

export const SIMPLE_APP_IDS = [
  'xchange',
  'xbuy',
  'xstays',
  'xcourier',
  'xproxy',
  'xevents',
];

export const isSimpleConsumerApp = appId => SIMPLE_APP_IDS.includes(appId);

/** @type {Record<string, object>} */
export const simpleAppExperiences = {
  xchange: {
    displayName: 'xChange',
    headline: 'Swap cash, crypto, or send value online',
    promise: 'Meet up locally — or settle online with digital assets locked until both sides confirm.',
    bullets: [
      'Cash ↔ crypto nearby, or online-only transfers',
      'Optional non-custodial escrow for remote deals',
      'Message to agree terms — settle your way',
    ],
    exploreTitle: 'People looking to swap',
    createCta: 'Post what you need',
    createTitle: 'What do you want to exchange?',
    requireTerms: true,
    termsSource: 'exchange',
    formSteps: [
      {
        id: 'intent',
        title: 'What & how',
        fieldIds: ['direction', 'assetKind', 'exchangeMode', 'youGiveAsset', 'youGiveAmount', 'youWantAsset', 'youWantAmount'],
      },
      {
        id: 'settle',
        title: 'Settlement',
        fieldIds: ['settlementMethods', 'onlineFulfillment', 'timeWindow', 'area'],
      },
      {
        id: 'trust',
        title: 'Proof & notes',
        fieldIds: ['proofFlags', 'contactHint', 'notes'],
      },
    ],
    formFields: [
      {
        id: 'direction',
        label: 'I want to',
        type: 'select',
        required: true,
        optionsSource: 'xchangeDirection',
      },
      {
        id: 'assetKind',
        label: 'What’s involved',
        type: 'select',
        required: true,
        optionsSource: 'assetKind',
      },
      {
        id: 'exchangeMode',
        label: 'Exchange mode',
        type: 'select',
        required: true,
        optionsSource: 'exchangeMode',
      },
      {
        id: 'youGiveAsset',
        label: 'You give (asset)',
        type: 'select',
        required: true,
        optionsSource: 'xchangeAsset',
      },
      {
        id: 'youGiveAmount',
        label: 'You give (amount)',
        type: 'text',
        required: true,
        placeholder: 'e.g. 200 USDC or 50,000 JMD',
      },
      {
        id: 'youWantAsset',
        label: 'You want (asset)',
        type: 'select',
        required: true,
        optionsSource: 'xchangeAsset',
      },
      {
        id: 'youWantAmount',
        label: 'You want (amount)',
        type: 'text',
        required: true,
        placeholder: 'e.g. 50,000 cash or 200 USDC',
      },
      {
        id: 'settlementMethods',
        label: 'How to settle',
        type: 'multiselect',
        required: true,
        optionsSource: 'settlementMethods',
      },
      {
        id: 'onlineFulfillment',
        label: 'If online — how it completes',
        type: 'multiselect',
        optionsSource: 'onlineFulfillment',
      },
      {
        id: 'timeWindow',
        label: 'When',
        type: 'text',
        required: true,
        placeholder: 'e.g. Today after 5 pm, this weekend',
      },
      {
        id: 'area',
        label: 'Meetup area or online region',
        type: 'text',
        placeholder: 'Neighbourhood — or “online only / any timezone”',
      },
      {
        id: 'proofFlags',
        label: 'Proof you’ll accept',
        type: 'multiselect',
        optionsSource: 'xchangeProof',
      },
      {
        id: 'contactHint',
        label: 'Preferred contact',
        type: 'text',
        placeholder: 'WhatsApp, Telegram, in-app…',
      },
      {
        id: 'notes',
        label: 'Extra notes',
        type: 'textarea',
        placeholder: 'Rate flexibility, wallet network, escrow preference…',
      },
    ],
    demoItems: [
      {
        id: 'xc-1',
        title: '200 USDC → cash',
        meta: 'Half Way Tree · today',
        blurb: 'Looking for cash this afternoon. Flexible on exact spot.',
        badge: 'Near you',
        image: '/static/demo/cash.jpg',
      },
      {
        id: 'xc-2',
        title: 'Need 50,000 cash for USDC',
        meta: 'Online escrow OK · tomorrow',
        blurb: 'Prefer locked escrow then release. Can also meet at a café.',
        badge: 'Open',
        image: '/static/demo/cafe.jpg',
      },
      {
        id: 'xc-3',
        title: 'Send USDT online',
        meta: 'Remote · small amount',
        blurb: 'Wallet-to-wallet after chat confirm. First-timer friendly.',
        badge: 'Online',
        image: '/static/demo/meetup-people.jpg',
      },
    ],
    successTitle: 'You’re set to connect',
    successBody: 'In a live trade you’d message them next. This was a demo walkthrough.',
    technicalNotes: [
      'Optional: lock funds in non-custodial escrow until a handoff PIN or online confirm.',
      'Settlement can use community rails (DePay, gig, chain) or cash in person.',
      'Wallet connect is only needed if you choose on-chain escrow or crypto transfer.',
    ],
  },
  xbuy: {
    displayName: 'Buy & Sell',
    headline: 'Buy & sell everyday items',
    promise: 'Post what you’re selling — or browse what’s nearby.',
    bullets: [
      'List an item in a few taps',
      'Browse what others are offering',
      'Message when you’re ready — pay your way',
    ],
    exploreTitle: 'Things people are offering',
    createCta: 'Post an item',
    createTitle: 'What are you selling?',
    requireTerms: true,
    formSteps: [
      {
        id: 'basics',
        title: 'What is it?',
        fieldIds: ['listingType', 'categories', 'itemTitle', 'description', 'condition'],
      },
      {
        id: 'price',
        title: 'Price & specs',
        fieldIds: [
          'brandModel',
          'quantity',
          'priceType',
          'currency',
          'price',
          'yearOrAge',
          'dimensions',
        ],
      },
      {
        id: 'handoff',
        title: 'Handoff',
        fieldIds: [
          'area',
          'fulfillment',
          'exchangeMode',
          'settlementMethods',
          'onlineFulfillment',
          'availableFrom',
          'photosNote',
          'contactHint',
          'notes',
        ],
      },
    ],
    formFields: [
      {
        id: 'listingType',
        label: 'Listing type',
        type: 'select',
        required: true,
        optionsSource: 'saleListingType',
      },
      {
        id: 'categories',
        label: 'Category',
        type: 'multiselect',
        required: true,
        optionsSource: 'saleCategory',
      },
      {
        id: 'itemTitle',
        label: 'Title',
        type: 'text',
        required: true,
        placeholder: 'e.g. Mountain bike — 29" trail',
      },
      {
        id: 'description',
        label: 'Description',
        type: 'textarea',
        required: true,
        placeholder: 'What’s included, defects, age, why you’re selling…',
      },
      {
        id: 'condition',
        label: 'Condition',
        type: 'select',
        required: true,
        optionsSource: 'saleCondition',
      },
      {
        id: 'brandModel',
        label: 'Brand / model / size',
        type: 'text',
        placeholder: 'e.g. Trek Marlin 7, Medium',
      },
      {
        id: 'quantity',
        label: 'Quantity available',
        type: 'number',
        required: true,
        placeholder: '1',
        min: 1,
      },
      {
        id: 'priceType',
        label: 'Price type',
        type: 'select',
        required: true,
        optionsSource: 'salePriceType',
      },
      {
        id: 'currency',
        label: 'Currency',
        type: 'select',
        required: true,
        optionsSource: 'saleCurrency',
      },
      {
        id: 'price',
        label: 'Asking price',
        type: 'text',
        required: true,
        placeholder: 'e.g. 15,000',
      },
      {
        id: 'yearOrAge',
        label: 'Year / age',
        type: 'text',
        placeholder: 'e.g. 2021, 6 months old',
      },
      {
        id: 'dimensions',
        label: 'Size / dimensions / weight',
        type: 'text',
        placeholder: 'Helpful for delivery or shipping',
      },
      {
        id: 'area',
        label: 'Location / area',
        type: 'text',
        required: true,
        placeholder: 'Neighbourhood or parish',
      },
      {
        id: 'fulfillment',
        label: 'Handoff options',
        type: 'multiselect',
        required: true,
        optionsSource: 'saleFulfillment',
      },
      {
        id: 'exchangeMode',
        label: 'In person or online',
        type: 'select',
        required: true,
        optionsSource: 'exchangeMode',
      },
      {
        id: 'settlementMethods',
        label: 'Accepted payment / settlement',
        type: 'multiselect',
        required: true,
        optionsSource: 'settlementMethods',
      },
      {
        id: 'onlineFulfillment',
        label: 'If online — how delivery works',
        type: 'multiselect',
        optionsSource: 'onlineFulfillment',
      },
      {
        id: 'availableFrom',
        label: 'Available from',
        type: 'text',
        placeholder: 'e.g. Immediately, weekend',
      },
      {
        id: 'photosNote',
        label: 'Photos',
        type: 'text',
        placeholder: 'You’ll add photos when publishing (demo skips upload)',
      },
      {
        id: 'contactHint',
        label: 'Preferred contact',
        type: 'text',
        placeholder: 'WhatsApp, call after 5 pm…',
      },
      {
        id: 'notes',
        label: 'Extra notes',
        type: 'textarea',
        placeholder: 'Warranty, bundle deals, viewing times…',
      },
    ],
    demoItems: [
      {
        id: 'xb-1',
        title: 'Mountain bike — lightly used',
        meta: '15,000 · Portmore',
        blurb: 'Pickup this week. Can show photos in chat.',
        badge: 'For sale',
        image: '/static/demo/bike.jpg',
      },
      {
        id: 'xb-2',
        title: 'Weekend yard cleanup',
        meta: 'From 8,000 · Kingston',
        blurb: 'Tools included. Book Saturday or Sunday.',
        badge: 'Service',
        image: '/static/demo/yard.jpg',
      },
      {
        id: 'xb-3',
        title: 'Office desk + chair set',
        meta: 'Negotiable · New Kingston',
        blurb: 'Must pick up. Happy to hold for serious buyers.',
        badge: 'For sale',
        image: '/static/demo/desk.jpg',
      },
    ],
    successTitle: 'Item ready to post',
    successBody: 'In a live listing you’d publish next. Demo complete.',
    technicalNotes: [
      'Listings are informational — no checkout on the communications site.',
      'Optional escrow rails exist if both parties want them later.',
    ],
  },
  xstays: {
    displayName: 'Rentals',
    headline: 'Rent out what you have',
    promise: 'List a place, car, or gear. Renters message you when they need it.',
    bullets: [
      'Post what you’re willing to rent',
      'Browse what others listed',
      'Agree dates in chat — hand off simply',
    ],
    exploreTitle: 'Available to rent',
    createCta: 'Rent your item',
    createTitle: 'What are you renting out?',
    requireTerms: true,
    formSteps: [
      {
        id: 'basics',
        title: 'What is it?',
        fieldIds: ['assetType', 'itemTitle', 'description', 'condition'],
      },
      {
        id: 'rates',
        title: 'Rates & timing',
        fieldIds: [
          'rate',
          'rateUnit',
          'currency',
          'deposit',
          'capacity',
          'minPeriod',
          'maxPeriod',
          'checkInOut',
        ],
      },
      {
        id: 'terms',
        title: 'Rules & handoff',
        fieldIds: [
          'area',
          'availability',
          'exchangeMode',
          'settlementMethods',
          'onlineFulfillment',
          'bookingStyle',
          'cancellation',
          'lateOrDamage',
          'included',
          'requirements',
          'rules',
          'handoffNotes',
          'photosNote',
        ],
      },
    ],
    formFields: [
      {
        id: 'assetType',
        label: 'Rental type',
        type: 'select',
        required: true,
        optionsSource: 'rentalAssetType',
      },
      {
        id: 'itemTitle',
        label: 'Title',
        type: 'text',
        required: true,
        placeholder: 'e.g. Studio flat, compact car, camera kit',
      },
      {
        id: 'description',
        label: 'Description',
        type: 'textarea',
        required: true,
        placeholder: 'What’s included, capacity, features, restrictions…',
      },
      {
        id: 'condition',
        label: 'Condition',
        type: 'select',
        required: true,
        optionsSource: 'saleCondition',
      },
      {
        id: 'rate',
        label: 'Rate amount',
        type: 'text',
        required: true,
        placeholder: 'e.g. 12,000',
      },
      {
        id: 'rateUnit',
        label: 'Rate unit',
        type: 'select',
        required: true,
        optionsSource: 'rentalRateUnit',
      },
      {
        id: 'currency',
        label: 'Currency',
        type: 'select',
        required: true,
        optionsSource: 'saleCurrency',
      },
      {
        id: 'deposit',
        label: 'Refundable deposit',
        type: 'text',
        placeholder: 'e.g. 20,000 or none',
      },
      {
        id: 'capacity',
        label: 'Capacity (guests / seats / units)',
        type: 'text',
        placeholder: 'e.g. Sleeps 2, 5 seats, 1 kit',
      },
      {
        id: 'minPeriod',
        label: 'Minimum rental period',
        type: 'text',
        placeholder: 'e.g. 1 night, 4 hours, weekend',
      },
      {
        id: 'maxPeriod',
        label: 'Maximum rental period',
        type: 'text',
        placeholder: 'e.g. 2 weeks',
      },
      {
        id: 'checkInOut',
        label: 'Check-in / check-out or pickup window',
        type: 'text',
        placeholder: 'e.g. After 3 pm / before 11 am',
      },
      {
        id: 'area',
        label: 'Location / pickup area',
        type: 'text',
        required: true,
        placeholder: 'Neighbourhood or parish',
      },
      {
        id: 'availability',
        label: 'Availability',
        type: 'text',
        required: true,
        placeholder: 'e.g. Weekends, from next Friday',
      },
      {
        id: 'exchangeMode',
        label: 'Handoff mode',
        type: 'select',
        required: true,
        optionsSource: 'exchangeMode',
      },
      {
        id: 'settlementMethods',
        label: 'Accepted payment / settlement',
        type: 'multiselect',
        required: true,
        optionsSource: 'settlementMethods',
      },
      {
        id: 'onlineFulfillment',
        label: 'If online rental — access delivery',
        type: 'multiselect',
        optionsSource: 'onlineFulfillment',
      },
      {
        id: 'bookingStyle',
        label: 'Booking style',
        type: 'select',
        optionsSource: 'rentalBooking',
      },
      {
        id: 'cancellation',
        label: 'Cancellation policy',
        type: 'select',
        optionsSource: 'rentalCancel',
      },
      {
        id: 'lateOrDamage',
        label: 'Late return / damage fee',
        type: 'text',
        placeholder: 'e.g. 2,000 / hour late; damage billed at cost',
      },
      {
        id: 'included',
        label: 'What’s included',
        type: 'multiselect',
        optionsSource: 'rentalIncluded',
      },
      {
        id: 'requirements',
        label: 'Renter requirements',
        type: 'multiselect',
        optionsSource: 'rentalRequirements',
      },
      {
        id: 'rules',
        label: 'House / use rules',
        type: 'textarea',
        placeholder: 'No smoking, fuel return full, no pets…',
      },
      {
        id: 'handoffNotes',
        label: 'Handoff / return notes',
        type: 'textarea',
        placeholder: 'Meet at plaza, PIN on pickup, return by 6 pm…',
      },
      {
        id: 'photosNote',
        label: 'Photos',
        type: 'text',
        placeholder: 'You’ll add photos when publishing (demo skips upload)',
      },
    ],
    demoItems: [
      {
        id: 'xs-1',
        title: 'Studio near the waterfront',
        meta: '2 nights · from 12,000 / night',
        blurb: 'Self check-in. Quiet building. Wi‑Fi included.',
        badge: 'Stay',
        image: '/static/demo/studio.jpg',
      },
      {
        id: 'xs-2',
        title: 'Compact car — weekend',
        meta: 'Fri–Sun · fuel your own',
        blurb: 'Meet at plaza parking. Clean record required.',
        badge: 'Vehicle',
        image: '/static/demo/car.jpg',
      },
      {
        id: 'xs-3',
        title: 'Camera kit for events',
        meta: 'Day rate · deposit held until return',
        blurb: 'Includes lenses + bag. Pickup downtown.',
        badge: 'Gear',
        image: '/static/demo/camera.jpg',
      },
    ],
    successTitle: 'Rental listing drafted',
    successBody: 'You’d publish this next so renters can find it. Demo complete.',
    technicalNotes: [
      'Hosts may use a deposit held until check-out.',
      'Optional PIN at handoff proves pickup/return for escrow release.',
    ],
  },
  xcourier: {
    displayName: 'Services',
    layout: 'servicesTabs',
    headline: 'Services',
    promise: 'Find a service provider — on-site or online — or offer your trade.',
    bullets: [
      'Browse providers by trade (remote OK)',
      'Request help and choose how you pay — cash or digital assets',
      'Join as a provider when you’re ready',
    ],
    exploreTitle: 'Providers nearby',
    createCta: 'Request a provider',
    createTitle: 'What do you need?',
    secondaryLink: { label: 'Become a provider', href: '#become' },
    successTitle: 'Request ready',
    successBody: 'In a live request you’d connect next. Demo complete.',
    technicalNotes: [
      'Provider categories are shared for search and “Become a provider.”',
      'Posting a request opens demo quotes → accept → schedule → job thread → settle.',
      'Remote / online jobs can settle with stablecoins or non-custodial escrow.',
      'Providers may settle off-platform in chat; optional rails later.',
    ],
    tabs: {
      provider: {
        exploreTitle: 'Providers nearby',
        createCta: 'Request a provider',
        createTitle: 'What do you need?',
        requireTerms: true,
        formSteps: [
          {
            id: 'job',
            title: 'The job',
            fieldIds: ['categoryId', 'deliveryMode', 'urgency', 'scheduleWindow', 'matchMode', 'notes'],
          },
          {
            id: 'pay',
            title: 'Where & pay',
            fieldIds: [
              'area',
              'budget',
              'currency',
              'settlementMethods',
              'onlineFulfillment',
              'useEscrow',
              'escrowMilestone',
              'escrowAmount',
              'contactHint',
            ],
          },
        ],
        formFields: [
          {
            id: 'categoryId',
            label: 'Service type',
            type: 'serviceCategory',
            required: true,
          },
          {
            id: 'deliveryMode',
            label: 'On-site or online',
            type: 'select',
            required: true,
            optionsSource: 'serviceDelivery',
          },
          {
            id: 'urgency',
            label: 'When needed',
            type: 'select',
            required: true,
            optionsSource: 'serviceUrgency',
          },
          {
            id: 'scheduleWindow',
            label: 'Preferred schedule window',
            type: 'select',
            required: true,
            optionsSource: 'scheduleWindow',
          },
          {
            id: 'matchMode',
            label: 'Matching style',
            type: 'select',
            optionsSource: 'matchMode',
          },
          {
            id: 'useEscrow',
            label: 'Use escrow?',
            type: 'select',
            options: [
              { value: 'no', label: 'No — settle in chat' },
              { value: 'yes', label: 'Yes — lock digital assets / deposit' },
            ],
          },
          {
            id: 'escrowMilestone',
            label: 'Escrow milestone',
            type: 'select',
            optionsSource: 'escrowMilestone',
          },
          {
            id: 'escrowAmount',
            label: 'Escrow / deposit amount',
            type: 'text',
            placeholder: 'e.g. 5,000 or leave blank to use quote',
          },
          {
            id: 'notes',
            label: 'Describe the job',
            type: 'textarea',
            required: true,
            placeholder: 'What’s needed, constraints, access…',
          },
          {
            id: 'area',
            label: 'Area (or “online only”)',
            type: 'text',
            placeholder: 'Neighbourhood / parish — or remote',
          },
          {
            id: 'budget',
            label: 'Budget / rate hint',
            type: 'text',
            placeholder: 'e.g. 8,000 or 50 USDC',
          },
          {
            id: 'currency',
            label: 'Currency / asset',
            type: 'select',
            optionsSource: 'settlementCurrency',
          },
          {
            id: 'settlementMethods',
            label: 'How you’ll pay',
            type: 'multiselect',
            required: true,
            optionsSource: 'settlementMethods',
          },
          {
            id: 'onlineFulfillment',
            label: 'If remote — how work is delivered',
            type: 'multiselect',
            optionsSource: 'onlineFulfillment',
          },
          {
            id: 'contactHint',
            label: 'Preferred contact',
            type: 'text',
            placeholder: 'WhatsApp, call after 5…',
          },
        ],
        becomeFormSteps: [
          {
            id: 'offer',
            title: 'Your offer',
            fieldIds: ['becomeCategory', 'displayName', 'deliveryModes', 'coverage'],
          },
          {
            id: 'rates',
            title: 'Rates & settlement',
            fieldIds: ['rateHint', 'settlementMethods', 'onlineFulfillment', 'bio'],
          },
        ],
        becomeFormFields: [
          {
            id: 'becomeCategory',
            label: 'Your service',
            type: 'serviceCategory',
            required: true,
          },
          {
            id: 'displayName',
            label: 'Display name',
            type: 'text',
            required: true,
            placeholder: 'Business or your name',
          },
          {
            id: 'deliveryModes',
            label: 'How you deliver',
            type: 'multiselect',
            required: true,
            optionsSource: 'serviceDeliveryMulti',
          },
          {
            id: 'coverage',
            label: 'Areas / online availability',
            type: 'text',
            required: true,
            placeholder: 'e.g. Kingston, Portmore — or island-wide online',
          },
          {
            id: 'rateHint',
            label: 'Typical rate',
            type: 'text',
            placeholder: 'e.g. From 5,000 / visit or hourly',
          },
          {
            id: 'settlementMethods',
            label: 'Payment you accept',
            type: 'multiselect',
            required: true,
            optionsSource: 'settlementMethods',
          },
          {
            id: 'onlineFulfillment',
            label: 'If online — how you deliver work',
            type: 'multiselect',
            optionsSource: 'onlineFulfillment',
          },
          {
            id: 'bio',
            label: 'Short bio',
            type: 'textarea',
            placeholder: 'Experience, licenses, typical jobs…',
          },
        ],
      },
    },
    formFields: [],
    demoItems: [],
  },
  xproxy: {
    displayName: 'Payers & Delivery',
    layout: 'payersDeliveryTabs',
    headline: 'Payers & Delivery',
    promise: 'Request someone to pay for something, prep, package, or deliver.',
    bullets: [
      'Payer: someone buys or pays on your behalf',
      'Courier: small deliveries across town',
      'Big Move: furniture, offices, full loads',
    ],
    exploreTitle: 'Open requests',
    createCta: 'Request a runner',
    createTitle: 'What do you need?',
    secondaryLink: { label: 'Become a runner', href: '/landing/run' },
    successTitle: 'Runner request ready',
    successBody: 'In production a runner would claim this. Demo complete.',
    technicalNotes: [
      'Payer runs may lock a budget until handoff PIN.',
      'Courier and Big Move can fund on the gig rail or settle in chat.',
    ],
    tabs: {
      payer: {
        exploreTitle: 'Payer jobs nearby',
        createCta: 'Request a payer',
        createTitle: 'What should they pay or buy?',
        successTitle: 'Payer request drafted',
        successBody: 'A runner would claim this next. Demo complete.',
        
          formSteps: [
            {
              id: 'what',
              title: 'What to get',
              fieldIds: [
                'purpose',
                'categories',
                'itemTitle',
                'itemSpecs',
                'condition',
                'quantity',
              ],
            },
            {
              id: 'budget',
              title: 'Budget & where',
              fieldIds: [
                'unitBudget',
                'cashBudget',
                'paymentMethod',
                'merchantName',
                'merchantAddress',
                'area',
              ],
            },
            {
              id: 'after',
              title: 'After they pay',
              fieldIds: [
                'fulfillment',
                'exchangeMode',
                'settlementMethods',
                'onlineFulfillment',
                'substitutions',
                'proofFlags',
                'deadline',
                'merchantContact',
                'handoffContact',
                'purchaseDesc',
                'useEscrow',
                'escrowMilestone',
                'escrowAmount',
              ],
            },
          ],
          formFields: [
          {
            id: 'purpose',
            label: 'What do you need done?',
            type: 'multiselect',
            required: true,
            optionsSource: 'payerPurpose',
          },
          {
            id: 'categories',
            label: 'Category',
            type: 'multiselect',
            required: true,
            optionsSource: 'payerCategory',
          },
          {
            id: 'itemTitle',
            label: 'Item / bill title',
            type: 'text',
            required: true,
            placeholder: 'e.g. iPhone 13 case, JPS bill A/C 123…',
          },
          {
            id: 'itemSpecs',
            label: 'Exact specs / account details',
            type: 'textarea',
            required: true,
            placeholder:
              'Brand, model, size, colour, SKU, account #, customer name on bill, reference number…',
          },
          {
            id: 'condition',
            label: 'Condition expected',
            type: 'select',
            required: true,
            optionsSource: 'payerCondition',
          },
          {
            id: 'quantity',
            label: 'Quantity',
            type: 'number',
            required: true,
            placeholder: 'e.g. 1',
            min: 1,
          },
          {
            id: 'unitBudget',
            label: 'Target / max unit price',
            type: 'text',
            placeholder: 'e.g. 4,500 each',
          },
          {
            id: 'cashBudget',
            label: 'Total cash budget (incl. tax / fees)',
            type: 'text',
            required: true,
            placeholder: 'e.g. 8,000 — do not exceed',
          },
          {
            id: 'paymentMethod',
            label: 'How should they pay?',
            type: 'select',
            required: true,
            optionsSource: 'payerPaymentMethod',
          },
          {
            id: 'merchantName',
            label: 'Merchant / payee name',
            type: 'text',
            required: true,
            placeholder: 'Shop, utility, landlord, clinic…',
          },
          {
            id: 'merchantAddress',
            label: 'Where (address or landmark)',
            type: 'text',
            required: true,
            placeholder: 'Street, plaza, branch, parish',
          },
          {
            id: 'area',
            label: 'Area / neighbourhood',
            type: 'text',
            required: true,
            placeholder: 'e.g. Half Way Tree, Portmore',
          },
          {
            id: 'fulfillment',
            label: 'After paying',
            type: 'select',
            required: true,
            optionsSource: 'payerFulfillment',
          },
          {
            id: 'exchangeMode',
            label: 'In person runner or online only',
            type: 'select',
            required: true,
            optionsSource: 'exchangeMode',
          },
          {
            id: 'settlementMethods',
            label: 'How you fund / settle',
            type: 'multiselect',
            required: true,
            optionsSource: 'settlementMethods',
          },
          {
            id: 'onlineFulfillment',
            label: 'If online — completion method',
            type: 'multiselect',
            optionsSource: 'onlineFulfillment',
          },
          {
            id: 'substitutions',
            label: 'If exact item unavailable',
            type: 'select',
            required: true,
            optionsSource: 'payerSubstitution',
          },
          {
            id: 'proofFlags',
            label: 'Proof required',
            type: 'multiselect',
            optionsSource: 'payerProof',
          },
          {
            id: 'deadline',
            label: 'Needed by',
            type: 'text',
            required: true,
            placeholder: 'e.g. Today before 5 pm',
          },
          {
            id: 'merchantContact',
            label: 'Merchant / site contact (optional)',
            type: 'text',
            placeholder: 'Name / phone at the counter',
          },
          {
            id: 'handoffContact',
            label: 'Your contact for updates',
            type: 'text',
            placeholder: 'Phone or WhatsApp for the runner',
          },
          {
            id: 'purchaseDesc',
            label: 'Extra instructions',
            type: 'textarea',
            placeholder: 'Parking, queue number, password for account, “ask for Miss X”…',
          },
          {
            id: 'useEscrow',
            label: 'Lock budget in escrow?',
            type: 'select',
            options: [
              { value: 'no', label: 'No — pay runner in chat' },
              { value: 'yes', label: 'Yes — lock until PIN / confirm' },
            ],
          },
          {
            id: 'escrowMilestone',
            label: 'Escrow milestone',
            type: 'select',
            optionsSource: 'escrowMilestone',
          },
          {
            id: 'escrowAmount',
            label: 'Escrow / budget lock amount',
            type: 'text',
            placeholder: 'Defaults to cash budget if blank',
          },
        ],
        addons: [
          {
            id: 'prepping',
            label: 'Prepping',
            placeholder: 'What should they prep? Ingredients, tools, setup…',
          },
          {
            id: 'packing',
            label: 'Packing',
            placeholder: 'How should it be packed or wrapped?',
          },
          {
            id: 'delivery',
            label: 'Delivery',
            placeholder: 'Where and how should it be delivered after payment?',
          },
        ],
        demoItems: [
          {
            id: 'xp-1',
            title: 'Buy phone charger + case',
            meta: 'Budget 4,500 · downtown',
            blurb: 'Any reputable shop. Text photos before paying.',
            badge: 'Errand',
            image: '/static/demo/electronics.jpg',
          },
          {
            id: 'xp-2',
            title: 'Collect package from post office',
            meta: 'ID on file · afternoon',
            blurb: 'Need someone with flexible schedule today.',
            badge: 'Pickup',
            image: '/static/demo/package.jpg',
          },
          {
            id: 'xp-3',
            title: 'Pay utility bill + receipt',
            meta: 'Budget 12,000 · Half Way Tree',
            blurb: 'Photo of stamped receipt required.',
            badge: 'Pay',
            image: '/static/demo/cash.jpg',
          },
        ],
      },
      courier: {
        exploreTitle: 'Recent deliveries nearby',
        createCta: 'Book a courier',
        createTitle: 'What are you sending?',
        successTitle: 'Courier request ready',
        successBody: 'In production a runner would claim this. Demo complete.',
        
          formSteps: [
            {
              id: 'package',
              title: 'Package',
              fieldIds: [
                'courierItems',
                'packageCount',
                'weightKg',
                'dimensions',
                'packageDesc',
                'handlingFlags',
              ],
            },
            {
              id: 'route',
              title: 'Pickup & drop-off',
              fieldIds: [
                'pickupContact',
                'dropoffContact',
                'timeWindow',
                'instructions',
                'settlementMethods',
              ],
            },
          ],
          formFields: [
          { id: 'pickup', label: 'Pickup', type: 'text', required: true, placeholder: 'Address or landmark' },
          { id: 'dropoff', label: 'Drop-off', type: 'text', required: true, placeholder: 'Address or landmark' },
          {
            id: 'courierItems',
            label: 'What’s being picked up',
            type: 'multiselect',
            required: true,
            optionsSource: 'courierItems',
          },
          {
            id: 'packageCount',
            label: 'Number of packages',
            type: 'number',
            required: true,
            placeholder: 'e.g. 2',
            min: 1,
          },
          {
            id: 'weightKg',
            label: 'Total weight (kg)',
            type: 'number',
            required: true,
            placeholder: 'e.g. 5',
            min: 0.1,
            step: 0.1,
            hint: 'Courier runs are for up to 30 kg total. Heavier loads should use Big Move.',
          },
          {
            id: 'dimensions',
            label: 'Largest package size (L × W × H)',
            type: 'text',
            placeholder: 'e.g. 40 × 30 × 20 cm',
          },
          {
            id: 'packageDesc',
            label: 'Package description',
            type: 'textarea',
            required: true,
            placeholder: 'Contents overview, brand, colour, any serial labels…',
          },
          {
            id: 'handlingFlags',
            label: 'Handling',
            type: 'multiselect',
            optionsSource: 'courierHandling',
          },
          {
            id: 'pickupContact',
            label: 'Pickup contact name / phone',
            type: 'text',
            placeholder: 'Who hands it over?',
          },
          {
            id: 'dropoffContact',
            label: 'Drop-off contact name / phone',
            type: 'text',
            placeholder: 'Who receives it?',
          },
          {
            id: 'timeWindow',
            label: 'Preferred time window',
            type: 'text',
            placeholder: 'e.g. Today 2–5 pm',
          },
          {
            id: 'instructions',
            label: 'Delivery notes',
            type: 'textarea',
            placeholder: 'Gate code, call on arrival, leave with security…',
          },
          {
            id: 'settlementMethods',
            label: 'How you’ll pay the runner',
            type: 'multiselect',
            required: true,
            optionsSource: 'settlementMethods',
          },
        ],
        addons: [
          {
            id: 'prepping',
            label: 'Prepping',
            placeholder: 'What should they prep before pickup?',
          },
          {
            id: 'packing',
            label: 'Packing',
            placeholder: 'How should it be packed?',
          },
        ],
        demoItems: [
          {
            id: 'xc-a',
            title: 'Documents → New Kingston',
            meta: '~35 min · sample quote 1,200',
            blurb: 'Envelope only. Recipient will be at reception.',
            badge: 'Same day',
            image: '/static/demo/envelope.jpg',
          },
          {
            id: 'xc-b',
            title: 'Grocery run — Portmore',
            meta: '~50 min · sample quote 1,800',
            blurb: 'Two bags. Keep cold items upright.',
            badge: 'Open',
            image: '/static/demo/groceries.jpg',
          },
          {
            id: 'xc-c',
            title: 'Phone repair pickup',
            meta: 'Ready now · sample quote 900',
            blurb: 'Collect from shop, deliver to home.',
            badge: 'Quick',
            image: '/static/demo/phone.jpg',
          },
        ],
      },
      bigMove: {
        exploreTitle: 'Big moves nearby',
        createCta: 'Request a big move',
        createTitle: 'What are you moving?',
        successTitle: 'Big move request ready',
        successBody: 'A crew would quote and claim this next. Demo complete.',
        
          formSteps: [
            {
              id: 'load',
              title: 'What to move',
              fieldIds: [
                'moveType',
                'cargoItems',
                'itemCount',
                'weightKg',
                'volumeHint',
                'cargoDesc',
              ],
            },
            {
              id: 'logistics',
              title: 'How & when',
              fieldIds: ['vehicle', 'helpers', 'accessFlags', 'moveDate', 'notes', 'settlementMethods'],
            },
          ],
          formFields: [
          {
            id: 'moveType',
            label: 'Move category',
            type: 'select',
            required: true,
            optionsSource: 'bigMoveTypes',
          },
          {
            id: 'cargoItems',
            label: 'What’s included',
            type: 'multiselect',
            required: true,
            optionsSource: 'bigMoveCargo',
          },
          { id: 'from', label: 'From', type: 'text', required: true, placeholder: 'Pickup address' },
          { id: 'to', label: 'To', type: 'text', required: true, placeholder: 'Drop-off address' },
          {
            id: 'itemCount',
            label: 'Approx. number of items / pieces',
            type: 'number',
            required: true,
            placeholder: 'e.g. 12',
            min: 1,
          },
          {
            id: 'weightKg',
            label: 'Estimated total weight (kg)',
            type: 'number',
            placeholder: 'e.g. 250',
            min: 1,
          },
          {
            id: 'volumeHint',
            label: 'Load size',
            type: 'select',
            required: true,
            options: [
              { value: 'few_items', label: 'A few bulky items' },
              { value: 'room', label: 'About 1 room' },
              { value: 'small_home', label: 'Small apartment / studio' },
              { value: 'full_home', label: 'Full home' },
              { value: 'commercial_load', label: 'Commercial / industrial load' },
              { value: 'disposal_load', label: 'Disposal / debris load' },
            ],
          },
          {
            id: 'vehicle',
            label: 'Vehicle needed',
            type: 'select',
            optionsSource: 'bigMoveVehicles',
          },
          {
            id: 'helpers',
            label: 'Helpers needed',
            type: 'select',
            options: [
              { value: '0', label: 'Driver only' },
              { value: '1', label: '1 helper' },
              { value: '2', label: '2 helpers' },
              { value: '3plus', label: '3+ helpers / crew' },
            ],
          },
          {
            id: 'accessFlags',
            label: 'Access at pickup / drop-off',
            type: 'multiselect',
            optionsSource: 'bigMoveAccess',
          },
          {
            id: 'moveDate',
            label: 'Preferred date / window',
            type: 'text',
            placeholder: 'e.g. Saturday morning',
          },
          {
            id: 'cargoDesc',
            label: 'Description of goods',
            type: 'textarea',
            required: true,
            placeholder: 'List key pieces, special handling, disposal vs relocate…',
          },
          {
            id: 'notes',
            label: 'Delivery notes',
            type: 'textarea',
            placeholder: 'Stairs, truck size, gate codes, contact on site…',
          },
          {
            id: 'settlementMethods',
            label: 'How you’ll pay the runner',
            type: 'multiselect',
            required: true,
            optionsSource: 'settlementMethods',
          },
        ],
        addons: [
          {
            id: 'prepping',
            label: 'Prepping',
            placeholder: 'Disassemble furniture, clear paths, label rooms…',
          },
          {
            id: 'packing',
            label: 'Packing',
            placeholder: 'Boxes, wrapping, fragile items…',
          },
        ],
        demoItems: [
          {
            id: 'bm-1',
            title: '1-bedroom apartment move',
            meta: 'Kingston → Portmore · truck + 2 helpers',
            blurb: 'Saturday morning preferred. Stairs at both ends.',
            badge: 'Apartment',
            image: '/static/demo/moving.jpg',
          },
          {
            id: 'bm-2',
            title: 'Office desks & chairs',
            meta: 'New Kingston · half day',
            blurb: 'Lift access. Need careful packing of monitors.',
            badge: 'Office',
            image: '/static/demo/desk.jpg',
          },
          {
            id: 'bm-3',
            title: 'Sofa + fridge only',
            meta: 'Spanish Town · same day',
            blurb: 'Ground floor both sides. Soft straps required.',
            badge: 'Furniture',
            image: '/static/demo/studio.jpg',
          },
        ],
      },
    },
    formFields: [],
    demoItems: [],
  },
  xevents: {
    displayName: 'Events',
    headline: 'Find events — or host one',
    promise: 'In person, online, or hybrid. Tickets and access can settle with cash or digital assets.',
    bullets: [
      'Browse meetups and ticketed events',
      'RSVP or request a ticket — door or digital pass',
      'Hosts can publish in a few taps',
    ],
    exploreTitle: 'Coming up',
    createCta: 'Host an event',
    createTitle: 'Event basics',
    requireTerms: true,
    termsSource: 'event',
    formSteps: [
      {
        id: 'basics',
        title: 'What & when',
        fieldIds: ['eventFormat', 'categories', 'eventTitle', 'description', 'eventDate', 'eventTime'],
      },
      {
        id: 'access',
        title: 'Where & tickets',
        fieldIds: ['venue', 'onlineLink', 'capacity', 'admission', 'price', 'currency', 'ticketDelivery'],
      },
      {
        id: 'settle',
        title: 'Pay & host',
        fieldIds: ['settlementMethods', 'hostContact', 'notes'],
      },
    ],
    formFields: [
      {
        id: 'eventFormat',
        label: 'Format',
        type: 'select',
        required: true,
        optionsSource: 'eventFormat',
      },
      {
        id: 'categories',
        label: 'Category',
        type: 'multiselect',
        required: true,
        optionsSource: 'eventCategory',
      },
      {
        id: 'eventTitle',
        label: 'Event name',
        type: 'text',
        required: true,
        placeholder: 'e.g. Friday night market',
      },
      {
        id: 'description',
        label: 'Description',
        type: 'textarea',
        required: true,
        placeholder: 'Who it’s for, agenda, what to bring…',
      },
      {
        id: 'eventDate',
        label: 'Date',
        type: 'date',
        required: true,
      },
      {
        id: 'eventTime',
        label: 'Start time / window',
        type: 'text',
        required: true,
        placeholder: 'e.g. 6:00 pm – 9:00 pm',
      },
      {
        id: 'venue',
        label: 'Venue / area (if in person)',
        type: 'text',
        placeholder: 'Venue name or neighbourhood',
      },
      {
        id: 'onlineLink',
        label: 'Online access (if remote)',
        type: 'text',
        placeholder: 'Zoom / stream link — or “sent after RSVP”',
      },
      {
        id: 'capacity',
        label: 'Capacity',
        type: 'text',
        placeholder: 'e.g. 40 people, unlimited online',
      },
      {
        id: 'admission',
        label: 'Admission',
        type: 'select',
        required: true,
        optionsSource: 'eventAdmission',
      },
      {
        id: 'price',
        label: 'Ticket / donation amount',
        type: 'text',
        placeholder: 'e.g. 1,500 or free',
      },
      {
        id: 'currency',
        label: 'Currency / asset',
        type: 'select',
        optionsSource: 'settlementCurrency',
      },
      {
        id: 'ticketDelivery',
        label: 'How tickets / access are delivered',
        type: 'multiselect',
        required: true,
        optionsSource: 'eventTicketDelivery',
      },
      {
        id: 'settlementMethods',
        label: 'Accepted payment',
        type: 'multiselect',
        optionsSource: 'settlementMethods',
      },
      {
        id: 'hostContact',
        label: 'Host contact',
        type: 'text',
        placeholder: 'WhatsApp, email…',
      },
      {
        id: 'notes',
        label: 'Extra notes',
        type: 'textarea',
        placeholder: 'Dress code, rain plan, refund policy…',
      },
    ],
    demoItems: [
      {
        id: 'xe-1',
        title: 'Saturday makers market',
        meta: 'Free · Emancipation Park · 10am',
        blurb: 'Food, crafts, live music. Family friendly.',
        badge: 'Free',
        image: '/static/demo/market.jpg',
      },
      {
        id: 'xe-2',
        title: 'Founders meetup',
        meta: 'Ticketed · coworking loft + online',
        blurb: 'Hybrid. Digital pass after payment. Bring a one-line pitch.',
        badge: 'Hybrid',
        image: '/static/demo/coworking.jpg',
      },
      {
        id: 'xe-3',
        title: 'Crypto 101 — live stream',
        meta: 'Online only · donation in USDC OK',
        blurb: 'Access link after RSVP. Escrow optional for paid seats.',
        badge: 'Online',
        image: '/static/demo/yoga.jpg',
      },
    ],
    successTitle: 'You’re on the list (demo)',
    successBody: 'Live events would send a pass or PIN. Demo complete.',
    technicalNotes: [
      'Paid events may issue access PINs after payment on a settlement rail.',
      'Online tickets can use digital passes or escrow release on confirm.',
      'Hosting uses a merchant persona when you go live — not required for browsing.',
    ],
  },

};

export const getSimpleAppExperience = appId => simpleAppExperiences[appId] || null;
